<template>
	<v-menu
		v-model="menuOpen"
		:close-on-content-click="false"
		location="bottom end"
		offset="4"
	>
		<template #activator="{ props: menuProps }">
			<!-- @mousedown.prevent keeps the currently-focused value input
			     focused (so its caret / selection survives) while the menu opens
			     and symbols are clicked. -->
			<span
				v-bind="menuProps"
				class="symbol-picker-trigger"
				:class="{ 'symbol-picker-trigger--active': menuOpen }"
				role="button"
				tabindex="-1"
				title="Insert symbol"
				@mousedown.prevent
				@click.stop
				>Ω</span
			>
		</template>

		<v-card class="symbol-picker-card pa-2" elevation="8" @mousedown.prevent>
			<div class="symbol-picker-header">
				<span class="symbol-picker-label">Insert symbol</span>
				<v-icon
					size="14"
					class="symbol-picker-close"
					title="Close"
					@click="menuOpen = false"
				>
					mdi-close
				</v-icon>
			</div>

			<div v-if="recent.length" class="symbol-picker-section">
				<div class="symbol-picker-label">Recently used</div>
				<div class="symbol-picker-grid">
					<button
						v-for="c in recent"
						:key="'recent-' + c"
						type="button"
						class="symbol-picker-cell"
						@mousedown.prevent
						@click="choose(c)"
					>
						{{ c }}
					</button>
				</div>
			</div>

			<div
				v-for="group in groups"
				:key="group.label"
				class="symbol-picker-section"
			>
				<div class="symbol-picker-label">{{ group.label }}</div>
				<div class="symbol-picker-grid">
					<button
						v-for="s in group.symbols"
						:key="s.char"
						type="button"
						class="symbol-picker-cell"
						:title="s.name"
						@mousedown.prevent
						@click="choose(s.char)"
					>
						{{ s.char }}
					</button>
				</div>
			</div>
		</v-card>
	</v-menu>
</template>

<script>
import { SYMBOL_GROUPS } from "@/utils/symbols";

const RECENT_KEY = "sde-symbol-picker-recent";
const RECENT_MAX = 12;

export default {
	name: "SymbolPicker",

	emits: ["insert", "menu-toggle"],

	data() {
		return {
			menuOpen: false,
			groups: SYMBOL_GROUPS,
			recent: [],
		};
	},

	watch: {
		// Refresh recents each time it opens so a symbol used elsewhere (same
		// localStorage) shows up without a remount. Also report open state so
		// hosts with outside-click handling (e.g. MetadataDialog) can suppress
		// it -- the menu content is teleported to body, so clicks inside it
		// register as "outside" the host element.
		menuOpen(open) {
			if (open) this.recent = this.loadRecent();
			this.$emit("menu-toggle", open);
		},
	},

	created() {
		this.recent = this.loadRecent();
	},

	methods: {
		loadRecent() {
			try {
				const raw = window.localStorage.getItem(RECENT_KEY);
				const parsed = raw ? JSON.parse(raw) : [];
				return Array.isArray(parsed)
					? parsed.filter((c) => typeof c === "string").slice(0, RECENT_MAX)
					: [];
			} catch {
				return [];
			}
		},

		recordRecent(char) {
			const next = [char, ...this.recent.filter((c) => c !== char)].slice(
				0,
				RECENT_MAX,
			);
			this.recent = next;
			try {
				window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
			} catch {
				// Private mode / quota: keep the in-memory list, skip persistence.
			}
		},

		choose(char) {
			this.recordRecent(char);
			// Menu stays open (close-on-content-click is false) so multiple
			// symbols can be inserted in a row, Excel-dialog style. The host
			// keeps the value input focused via mousedown.prevent, so each
			// insert lands at the live caret.
			this.$emit("insert", char);
		},
	},
};
</script>

<style scoped lang="scss">
.symbol-picker-trigger {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	font-size: 12px;
	line-height: 1;
	font-weight: 600;
	cursor: pointer;
	border-radius: 3px;
	user-select: none;
	// Resting emphasis from the text ramp, not opacity (DCTL-2338).
	color: rgb(var(--v-theme-fg-2));

	&:hover,
	&--active {
		color: rgb(var(--v-theme-secondary));
		background: rgba(0, 0, 0, 0.06);
	}
}

.symbol-picker-card {
	max-width: 280px;
	max-height: 360px;
	overflow-y: auto;
}

// The card's mousedown.prevent also covers the close icon (the event bubbles
// up before the focus-change default action runs), so closing via the button
// never blurs the host field.
.symbol-picker-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 4px;
}

.symbol-picker-close {
	cursor: pointer;
	opacity: 0.6;

	&:hover {
		opacity: 1;
	}
}

.symbol-picker-section + .symbol-picker-section {
	margin-top: 6px;
}

.symbol-picker-label {
	font-size: 0.65rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	opacity: 0.6;
	margin-bottom: 2px;
}

.symbol-picker-grid {
	display: grid;
	grid-template-columns: repeat(11, 1fr);
	gap: 2px;
}

.symbol-picker-cell {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 22px;
	font-size: 0.95rem;
	border-radius: 3px;
	cursor: pointer;
	background: transparent;
	transition: background 0.1s ease;

	&:hover {
		background: rgba(0, 0, 0, 0.08);
	}
}
</style>
